import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  inject,
  runInInjectionContext,
} from '@angular/core';
import {
  NgDiagramConfig,
  NgDiagramModelService,
  NgDiagramNodeTemplateMap,
  NgDiagramViewportService,
  SelectionChangedEvent,
  initializeModel,
  provideNgDiagram,
} from 'ng-diagram';
import { TreeNodeTemplateComponent } from './tree-node-template.component';
import { DecisionTreeData, TreeNode, VariableDef } from '../shared/models/decisionTree.interface';
import { DecisionTreeService } from '../shared/services/decision-tree.service';
import { AppToastService } from '../shared/toast-service/toast-service';
import { C45SaveModelResponse } from '../shared/services/decision-tree.service';

const NODE_WIDTH = 170;
const NODE_HEIGHT = 50;
const X_GAP = 60;
const Y_GAP = 120;
const ROOT_NODE_ID = 'root';

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  nodeId: string | null;
  isRoot: boolean;
  isLeaf: boolean;
  hasChildren: boolean;
}

interface CopiedBranch {
  rootLabel: string;
  nodes: any[];
  edges: any[];
}

@Component({
  selector: 'app-decision-tree',
  templateUrl: './decision-tree.component.html',
  styleUrls: ['./decision-tree.component.scss'],
  standalone: false,
  providers: [provideNgDiagram()],
})
export class DecisionTreeComponent implements OnInit, AfterViewInit, OnDestroy {
  dataModel: ReturnType<typeof initializeModel> | null = null;
  config: NgDiagramConfig = {
    zoom: { zoomToFit: { onInit: true, padding: 80 } },
  };
  nodeTemplateMap = new NgDiagramNodeTemplateMap([['tree-node', TreeNodeTemplateComponent]]);

  selectedNodeId: string | null = null;
  selectedEdgeId: string | null = null;
  editText = '';

  /** Whether the current diagram has unsaved changes (mirrors WPF _hasUnsavedChanges) */
  hasUnsavedChanges = false;

  /** Display name shown in the nav bar (mirrors WPF CurrentTreeNameText) */
  currentTreeName = 'Decision Tree';

  /** Controls the in-app "unsaved changes" confirmation notification */
  unsavedConfirmVisible = false;

  /** Original model metadata loaded from the API (OutcomeColumn, FeatureOrder, Schema) */
  private originalModelData: DecisionTreeData | null = null;

  /** Mongo document id of the model currently in the editor — null if never saved / new tree */
  private currentSavedModelId: string | null = null;

  /** True while a save-to-DB request is in flight */
  isSaving = false;

  contextMenu: ContextMenuState = {
    visible: false,
    x: 0,
    y: 0,
    nodeId: null,
    isRoot: false,
    isLeaf: false,
    hasChildren: false,
  };

  /** In-memory branch clipboard (mirrors WPF SfeBranchClipboardService) */
  private _copiedBranch: CopiedBranch | null = null;

  get hasCopiedBranch(): boolean {
    return this._copiedBranch !== null;
  }

  /** Popup state for renaming an edge label */
  edgeContextMenu = {
    visible: false,
    x: 0,
    y: 0,
    edgeId: null as string | null,
  };
  edgeLabelText = '';

  /** Tracks collapsed node IDs for expand/collapse feature */
  private collapsedNodeIds = new Set<string>();

  /**
   * Stores nodes and edges removed when a node is collapsed,
   * keyed by the collapsed node ID so they can be restored on expand.
   */
  private hiddenDescendants = new Map<string, { nodes: any[]; edges: any[] }>();

  // ---------------------------------------------------------------------------
  // Mouse-wheel zoom
  // ---------------------------------------------------------------------------
  private readonly ZOOM_STEP = 0.035; // WPF: ZoomStep(10) * 0.35 / 100
  private readonly MIN_ZOOM  = 0.30;  // WPF: MinZoomFactor 30
  private readonly MAX_ZOOM  = 3.00;  // WPF: MaxZoomFactor 300

  /** Non-passive wheel handler — stored as arrow fn so removeEventListener matches. */
  private readonly onWheelHandler = (event: WheelEvent): void => {
    const currentScale = this.viewportService.scale();
    // deltaY < 0 → scroll up → zoom in  (mirrors WPF e.Delta > 0)
    // deltaY > 0 → scroll down → zoom out
    const delta = event.deltaY < 0 ? this.ZOOM_STEP : -this.ZOOM_STEP;
    const newScale = Math.min(this.MAX_ZOOM, Math.max(this.MIN_ZOOM, currentScale + delta));
    this.viewportService.zoom(newScale / currentScale);
    // Stop event reaching ng-diagram's internal pan-on-wheel handler
    event.stopPropagation();
    event.preventDefault();
  };

  private modelService = inject(NgDiagramModelService);
  private viewportService = inject(NgDiagramViewportService);

  constructor(
    private injector: Injector,
    private el: ElementRef<HTMLElement>,
    private decisionTreeService: DecisionTreeService,
    private toastService: AppToastService
  ) {}

  ngAfterViewInit(): void {
    // capture:true — fires before ng-diagram's internal wheel/pan listener
    // passive:false — allows preventDefault() to suppress native scroll
    this.el.nativeElement.addEventListener('wheel', this.onWheelHandler, { capture: true, passive: false });
  }

  ngOnInit(): void {
    this.decisionTreeService.getDecisionTreeData().subscribe((treeData: DecisionTreeData) => {
      this.originalModelData = treeData;
      this.currentSavedModelId = treeData.Id ?? null;
      const nodes: any[] = [];
      const edges: any[] = [];
      const totalLeaves = this.countLeaves(treeData.Tree);
      const totalWidth = totalLeaves * (NODE_WIDTH + X_GAP);
      this.buildGraph(treeData.Tree, ROOT_NODE_ID, '', 0, 0, totalWidth, nodes, edges);
      this.dataModel = runInInjectionContext(this.injector, () => initializeModel({ nodes, edges }));
      this.currentTreeName = 'Decision Tree';
      this.hasUnsavedChanges = false;
    });
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('wheel', this.onWheelHandler, { capture: true });
    this.collapsedNodeIds.clear();
    this.hiddenDescendants.clear();
  }

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------

  onSelectionChanged(event: SelectionChangedEvent): void {
    this.selectedNodeId = event.selectedNodes[0]?.id ?? null;
    this.selectedEdgeId = event.selectedEdges[0]?.id ?? null;
  }

  // ---------------------------------------------------------------------------
  // Context menu – open / close
  // ---------------------------------------------------------------------------

  @HostListener('contextmenu', ['$event'])
  onContextMenu(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const nodeEl = target.closest('[data-node-id]') as HTMLElement | null;

    if (nodeEl) {
      event.preventDefault();
      event.stopPropagation();
      this.closeEdgeContextMenu();
      const nodeId = nodeEl.getAttribute('data-node-id')!;
      this.openNodeContextMenu(nodeId, event.clientX, event.clientY);
    } else if (this.selectedEdgeId) {
      // Right-click outside a node while an edge is selected → open edge rename popup
      event.preventDefault();
      event.stopPropagation();
      this.closeContextMenu();
      this.openEdgeContextMenu(this.selectedEdgeId, event.clientX, event.clientY);
    } else {
      this.closeContextMenu();
      this.closeEdgeContextMenu();
    }
  }

  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.dt-context-menu') && !target.closest('.dt-edge-context-menu')) {
      this.closeContextMenu();
      this.closeEdgeContextMenu();
    }
  }

  private openNodeContextMenu(nodeId: string, x: number, y: number): void {
    const node = this.modelService.getNodeById(nodeId);
    if (!node) return;

    this.selectedNodeId = nodeId;
    this.editText = (node.data as any)['label'] ?? '';

    const childEdges = this.modelService.getConnectedEdges(nodeId);
    const hasChildren =
      childEdges.some((e: any) => e.source === nodeId) ||
      this.collapsedNodeIds.has(nodeId);
    const isLeaf = !!(node.data as any)['isLeaf'];

    this.contextMenu = {
      visible: true,
      x,
      y,
      nodeId,
      isRoot: nodeId === ROOT_NODE_ID,
      isLeaf,
      hasChildren,
    };
  }

  closeContextMenu(): void {
    this.contextMenu = { ...this.contextMenu, visible: false };
  }

  private openEdgeContextMenu(edgeId: string, x: number, y: number): void {
    const edge = this.modelService.getEdgeById(edgeId);
    if (!edge) return;
    this.edgeLabelText = (edge.data as any)?.['label'] ?? '';
    this.edgeContextMenu = { visible: true, x, y, edgeId };
  }

  closeEdgeContextMenu(): void {
    this.edgeContextMenu = { ...this.edgeContextMenu, visible: false };
  }

  applyEdgeEdit(): void {
    if (!this.edgeContextMenu.edgeId) return;
    const edge = this.modelService.getEdgeById(this.edgeContextMenu.edgeId);
    if (!edge) return;
    this.modelService.updateEdgeData(this.edgeContextMenu.edgeId, {
      ...(edge.data as any),
      label: this.edgeLabelText,
    });
    this.markDirty();
    this.closeEdgeContextMenu();
    this.toastService.show('Edge label updated.', { classname: 'bg-success text-light' });
  }

  // ---------------------------------------------------------------------------
  // Nav bar – Create (mirrors WPF TopBarCreateButton_Click + InitializeNewStarterTree)
  // ---------------------------------------------------------------------------

  /**
   * Creates a fresh starter tree, replacing the current diagram.
   * Mirrors WPF: TopBarCreateButton_Click → PromptToSaveIfDirty → InitializeNewStarterTree
   */
  createNewTree(): void {
    if (this.hasUnsavedChanges) {
      // Show the in-app notification instead of a native browser confirm dialog
      this.unsavedConfirmVisible = true;
      return;
    }
    this.executeCreateNewTree();
  }

  /** Called when the user confirms discarding unsaved changes from the notification */
  confirmDiscardAndCreate(): void {
    this.unsavedConfirmVisible = false;
    this.executeCreateNewTree();
  }

  /** Saves the current tree to the DB, then creates a new tree on success */
  saveAndCreate(): void {
    const tree = this.buildTreeFromDiagram();
    if (!tree) {
      this.unsavedConfirmVisible = false;
      this.executeCreateNewTree();
      return;
    }

    const payload: DecisionTreeData = {
      OutcomeColumn: this.originalModelData?.OutcomeColumn ?? '',
      FeatureOrder: this.originalModelData?.FeatureOrder ?? [],
      Schema: this.originalModelData?.Schema ?? [],
      Tree: tree,
    };

    this.isSaving = true;
    this.decisionTreeService.saveDecisionTree(payload, this.currentSavedModelId ?? undefined).subscribe({
      next: (res: C45SaveModelResponse) => {
        this.isSaving = false;
        this.currentSavedModelId = res.SavedModelId;
        this.unsavedConfirmVisible = false;
        this.toastService.show(`Saved (ID: ${res.SavedModelId}).`, { classname: 'bg-success text-light' });
        this.executeCreateNewTree();
      },
      error: () => {
        this.isSaving = false;
        this.toastService.show('Save failed — tree not created.', { classname: 'bg-danger text-light' });
      },
    });
  }

  /** Dismisses the unsaved-changes confirmation notification */
  dismissUnsavedConfirm(): void {
    this.unsavedConfirmVisible = false;
  }

  private executeCreateNewTree(): void {

    // Clear all existing nodes and edges from the live model
    const allEdgeIds = this.modelService.edges().map((e: any) => e.id);
    const allNodeIds = this.modelService.nodes().map((n: any) => n.id);
    if (allEdgeIds.length) this.modelService.deleteEdges(allEdgeIds);
    if (allNodeIds.length) this.modelService.deleteNodes(allNodeIds);

    // Reset hidden/collapsed state
    this.collapsedNodeIds.clear();
    this.hiddenDescendants.clear();
    this.selectedNodeId = null;
    this.selectedEdgeId = null;
    // Clear the saved doc ID so the next save POSTs as a new record,
    // but keep originalModelData so OutcomeColumn/FeatureOrder/Schema
    // are still available for the payload.
    this.currentSavedModelId = null;

    // Build starter tree: Root → Branch 1 → Leaf 1
    // (mirrors WPF BuildStarterTree)
    const rootId   = `starter_root_${Date.now()}`;
    const branchId = `starter_branch_${Date.now()}`;
    const leafId   = `starter_leaf_${Date.now()}`;

    const cx = 200; // horizontal centre for the starter tree
    const rowH = NODE_HEIGHT + Y_GAP;

    const starterNodes = [
      {
        id: rootId,
        type: 'tree-node',
        position: { x: cx, y: 0 },
        autoSize: true,
        data: { label: 'Root', isLeaf: false, isRoot: true },
      },
      {
        id: branchId,
        type: 'tree-node',
        position: { x: cx, y: rowH },
        autoSize: true,
        data: { label: 'Branch 1', isLeaf: false },
      },
      {
        id: leafId,
        type: 'tree-node',
        position: { x: cx, y: rowH * 2 },
        autoSize: true,
        data: { label: 'Leaf 1', isLeaf: true },
      },
    ];

    const starterEdges = [
      {
        id: `edge_${rootId}_${branchId}`,
        source: rootId,
        sourcePort: 'port-bottom',
        target: branchId,
        targetPort: 'port-top',
        routing: 'straight',
        data: { label: 'Link 2' },
      },
      {
        id: `edge_${branchId}_${leafId}`,
        source: branchId,
        sourcePort: 'port-bottom',
        target: leafId,
        targetPort: 'port-top',
        routing: 'straight',
        data: { label: 'Link 1' },
      },
    ];

    this.modelService.addNodes(starterNodes);
    this.modelService.addEdges(starterEdges);

    // Update nav bar display (mirrors WPF UpdateCurrentTreeDisplay + MarkTreeAsClean)
    this.currentTreeName = 'Unsaved';
    this.hasUnsavedChanges = false;

    this.toastService.show('New starter tree created.', { classname: 'bg-success text-light' });
  }

  // ---------------------------------------------------------------------------
  // Popup actions
  // ---------------------------------------------------------------------------

  /** Apply edited text to the selected node label */
  applyEdit(): void {
    const text = this.editText.trim();
    if (!text || !this.contextMenu.nodeId) return;

    const node = this.modelService.getNodeById(this.contextMenu.nodeId);
    if (!node) return;

    this.modelService.updateNodeData(this.contextMenu.nodeId, {
      ...node.data,
      label: text,
    });
    this.markDirty();
    this.closeContextMenu();
  }

  /** Add a new child node below the currently selected node */
  addChildNode(): void {
    if (!this.contextMenu.nodeId) return;

    const parentNode = this.modelService.getNodeById(this.contextMenu.nodeId);
    if (!parentNode) return;

    const newNodeId = `node_${Date.now()}`;
    const childEdges = this.modelService.getConnectedEdges(this.contextMenu.nodeId);
    const siblingCount = childEdges.filter((e: any) => e.source === this.contextMenu.nodeId).length;

    const xOffset = (siblingCount - 1) * (NODE_WIDTH + X_GAP);
    const newNode = {
      id: newNodeId,
      type: 'tree-node',
      position: {
        x: parentNode.position.x + xOffset,
        y: parentNode.position.y + NODE_HEIGHT + Y_GAP,
      },
      autoSize: true,
      data: { label: 'New Node', isLeaf: true },
    };

    const newEdge = {
      id: `edge_${this.contextMenu.nodeId}_${newNodeId}`,
      source: this.contextMenu.nodeId,
      sourcePort: 'port-bottom',
      target: newNodeId,
      targetPort: 'port-top',
      routing: 'straight',
      data: { label: 'New Edge' },
    };

    this.modelService.addNodes([newNode]);
    this.modelService.addEdges([newEdge]);

    // If the parent was a leaf, it now has children → promote it to a branch node
    if ((parentNode.data as any)['isLeaf']) {
      this.modelService.updateNodeData(this.contextMenu.nodeId, { ...(parentNode.data as any), isLeaf: false });
    }
    this.markDirty();
    this.closeContextMenu();
    this.toastService.show('Child node added.', { classname: 'bg-success text-light' });
  }

  /**
   * Adds a new branch node (non-leaf) plus a child leaf below the currently selected node.
   * "Add Node" in the context menu.
   */
  addNodeWithLeaf(): void {
    if (!this.contextMenu.nodeId) return;

    const parentNode = this.modelService.getNodeById(this.contextMenu.nodeId);
    if (!parentNode) return;

    const ts = Date.now();
    const newNodeId = `node_${ts}`;
    const newLeafId = `leaf_${ts}`;
    const childEdges = this.modelService.getConnectedEdges(this.contextMenu.nodeId);
    const siblingCount = childEdges.filter((e: any) => e.source === this.contextMenu.nodeId).length;

    const xOffset = (siblingCount - 1) * (NODE_WIDTH + X_GAP);

    const newNode = {
      id: newNodeId,
      type: 'tree-node',
      position: {
        x: parentNode.position.x + xOffset,
        y: parentNode.position.y + NODE_HEIGHT + Y_GAP,
      },
      autoSize: true,
      data: { label: 'New Node', isLeaf: false },
    };

    const newLeaf = {
      id: newLeafId,
      type: 'tree-node',
      position: {
        x: parentNode.position.x + xOffset,
        y: parentNode.position.y + (NODE_HEIGHT + Y_GAP) * 2,
      },
      autoSize: true,
      data: { label: 'New Leaf', isLeaf: true },
    };

    const edgeToNode = {
      id: `edge_${this.contextMenu.nodeId}_${newNodeId}`,
      source: this.contextMenu.nodeId,
      sourcePort: 'port-bottom',
      target: newNodeId,
      targetPort: 'port-top',
      routing: 'straight',
      data: { label: 'New Edge' },
    };

    const edgeToLeaf = {
      id: `edge_${newNodeId}_${newLeafId}`,
      source: newNodeId,
      sourcePort: 'port-bottom',
      target: newLeafId,
      targetPort: 'port-top',
      routing: 'straight',
      data: { label: 'New Edge' },
    };

    this.modelService.addNodes([newNode, newLeaf]);
    this.modelService.addEdges([edgeToNode, edgeToLeaf]);

    // If the parent was a leaf, promote it to a branch node
    if ((parentNode.data as any)['isLeaf']) {
      this.modelService.updateNodeData(this.contextMenu.nodeId, { ...(parentNode.data as any), isLeaf: false });
    }
    this.markDirty();
    this.closeContextMenu();
    this.toastService.show('Node with leaf added.', { classname: 'bg-success text-light' });
  }

  /**
   * Adds a new leaf node directly below the currently selected node.
   * "Add Leaf" in the context menu.
   */
  addLeafNode(): void {
    if (!this.contextMenu.nodeId) return;

    const parentNode = this.modelService.getNodeById(this.contextMenu.nodeId);
    if (!parentNode) return;

    const newLeafId = `leaf_${Date.now()}`;
    const childEdges = this.modelService.getConnectedEdges(this.contextMenu.nodeId);
    const siblingCount = childEdges.filter((e: any) => e.source === this.contextMenu.nodeId).length;

    const xOffset = (siblingCount - 1) * (NODE_WIDTH + X_GAP);
    const newLeaf = {
      id: newLeafId,
      type: 'tree-node',
      position: {
        x: parentNode.position.x + xOffset,
        y: parentNode.position.y + NODE_HEIGHT + Y_GAP,
      },
      autoSize: true,
      data: { label: 'New Leaf', isLeaf: true },
    };

    const newEdge = {
      id: `edge_${this.contextMenu.nodeId}_${newLeafId}`,
      source: this.contextMenu.nodeId,
      sourcePort: 'port-bottom',
      target: newLeafId,
      targetPort: 'port-top',
      routing: 'straight',
      data: { label: 'New Edge' },
    };

    this.modelService.addNodes([newLeaf]);
    this.modelService.addEdges([newEdge]);

    // If the parent was a leaf, promote it to a branch node
    if ((parentNode.data as any)['isLeaf']) {
      this.modelService.updateNodeData(this.contextMenu.nodeId, { ...(parentNode.data as any), isLeaf: false });
    }
    this.markDirty();
    this.closeContextMenu();
    this.toastService.show('Leaf node added.', { classname: 'bg-success text-light' });
  }

  /** Remove the selected node and its entire subtree */
  removeNode(): void {
    const nodeId = this.contextMenu.nodeId;
    if (!nodeId) return;

    if (nodeId === ROOT_NODE_ID) {
      this.toastService.show('The root node cannot be removed.', { classname: 'bg-warning text-dark' });
      return;
    }

    const descendants = this.getAllDescendants(nodeId);
    const allNodeIds = [nodeId, ...descendants.map((n: any) => n.id)];

    const edgeIdsToDelete = new Set<string>();
    for (const id of allNodeIds) {
      const edges = this.modelService.getConnectedEdges(id);
      edges.forEach((e: any) => edgeIdsToDelete.add(e.id));
    }

    // Also include the edge coming INTO nodeId from its parent
    const allEdges = this.modelService.getConnectedEdges(nodeId);
    allEdges.forEach((e: any) => edgeIdsToDelete.add(e.id));

    this.modelService.deleteEdges([...edgeIdsToDelete]);
    this.modelService.deleteNodes(allNodeIds);

    // Clean up hidden descendants tracking
    this.collapsedNodeIds.delete(nodeId);
    this.hiddenDescendants.delete(nodeId);

    this.selectedNodeId = null;
    this.markDirty();
    this.closeContextMenu();
    this.toastService.show('Node removed.', { classname: 'bg-success text-light' });
  }

  /** Collapse (hide) or expand (show) children of the selected node */
  toggleCollapse(): void {
    const nodeId = this.contextMenu.nodeId;
    if (!nodeId) return;

    if (this.collapsedNodeIds.has(nodeId)) {
      this.expandNode(nodeId);
    } else {
      this.collapseNode(nodeId);
    }
    this.closeContextMenu();
  }

  get isSelectedNodeCollapsed(): boolean {
    return this.contextMenu.nodeId ? this.collapsedNodeIds.has(this.contextMenu.nodeId) : false;
  }

  // ---------------------------------------------------------------------------
  // Copy branch (mirrors WPF CopySelectedBranch / SfeBranchClipboardService)
  // Visible only for branch nodes: !isRoot && !isLeaf
  // ---------------------------------------------------------------------------

  /**
   * Copies the selected branch and its entire subtree into the in-memory clipboard.
   * Mirrors WPF: PopupCopyButton_Click → CopySelectedBranch → SfeBranchClipboardService.CopyBranch
   */
  copyBranch(): void {
    const nodeId = this.contextMenu.nodeId;
    if (!nodeId) return;

    const rootNode = this.modelService.getNodeById(nodeId);
    if (!rootNode) return;

    const descendants = this.getAllDescendants(nodeId);
    const allNodeIds = new Set<string>([nodeId, ...descendants.map((n: any) => n.id)]);

    // Collect only edges whose both endpoints are inside the subtree
    const seenEdgeIds = new Set<string>();
    const subtreeEdges: any[] = [];
    for (const id of allNodeIds) {
      for (const e of this.modelService.getConnectedEdges(id)) {
        if (!seenEdgeIds.has(e.id) && allNodeIds.has(e.source) && allNodeIds.has(e.target)) {
          subtreeEdges.push({ ...e, data: { ...(e.data ?? {}) } });
          seenEdgeIds.add(e.id);
        }
      }
    }

    this._copiedBranch = {
      rootLabel: (rootNode.data as any)['label'] ?? '',
      nodes: [rootNode, ...descendants].map((n: any) => ({ ...n, data: { ...(n.data as any) } })),
      edges: subtreeEdges,
    };

    this.closeContextMenu();
    this.toastService.show(`Branch "${this._copiedBranch.rootLabel}" copied.`, { classname: 'bg-info text-dark' });
  }

  // ---------------------------------------------------------------------------
  // Paste branch (mirrors WPF PasteSelectedBranch / SfeBranchClipboardService.CreatePasteClone)
  // Visible for root and branch nodes: !isLeaf
  // Disabled when no clipboard data OR target label equals copied branch label
  // ---------------------------------------------------------------------------

  /**
   * True when the clipboard has a branch AND its label differs from the current
   * target node label (case-insensitive).
   * Mirrors WPF: CanPasteBranchIntoSelectedNode()
   */
  get canPaste(): boolean {
    if (!this._copiedBranch || !this.contextMenu.nodeId) return false;
    const targetNode = this.modelService.getNodeById(this.contextMenu.nodeId);
    if (!targetNode) return false;
    const targetLabel: string = ((targetNode.data as any)['label'] ?? '').toLowerCase();
    return targetLabel !== this._copiedBranch.rootLabel.toLowerCase();
  }

  /**
   * Deep-clones the copied branch with fresh IDs, offsets positions relative to the
   * target node, then wires it as a new child of the target.
   * Mirrors WPF: PasteSelectedBranch → CreatePasteClone → PositionPastedBranchNearTarget
   */
  pasteBranch(): void {
    const nodeId = this.contextMenu.nodeId;
    if (!nodeId || !this._copiedBranch) return;

    const targetNode = this.modelService.getNodeById(nodeId);
    if (!targetNode) return;

    // Same-name guard (mirrors WPF check before insertion)
    const targetLabel: string = (targetNode.data as any)['label'] ?? '';
    if (targetLabel.toLowerCase() === this._copiedBranch.rootLabel.toLowerCase()) {
      this.toastService.show('Cannot paste a branch with the same name.', { classname: 'bg-warning text-dark' });
      return;
    }

    const ts = Date.now();

    // Build old-ID → new-ID mapping (mirrors WPF CloneNodeRecursive Guid.NewGuid)
    const idMap = new Map<string, string>();
    this._copiedBranch.nodes.forEach((n, i) => idMap.set(n.id, `pasted_${ts}_${i}`));

    // Pasted root is always the first entry (set by copyBranch)
    const originalRoot = this._copiedBranch.nodes[0];
    const rootOrigX: number = originalRoot.position?.x ?? 0;
    const rootOrigY: number = originalRoot.position?.y ?? 0;

    // Desired position: below-and-right of target (mirrors WPF desiredX/desiredY)
    const desiredX = targetNode.position.x + 40;
    const desiredY = targetNode.position.y + NODE_HEIGHT + Y_GAP;
    const deltaX = desiredX - rootOrigX;
    const deltaY = desiredY - rootOrigY;

    // Clone nodes with new IDs and offset positions
    const newNodes = this._copiedBranch.nodes.map((n: any) => ({
      ...n,
      id: idMap.get(n.id)!,
      position: {
        x: (n.position?.x ?? 0) + deltaX,
        y: (n.position?.y ?? 0) + deltaY,
      },
      data: { ...(n.data as any) },
    }));

    // Clone internal edges with new IDs and remapped source/target
    const newEdges = this._copiedBranch.edges.map((e: any, i: number) => ({
      ...e,
      id: `pasted_edge_${ts}_${i}`,
      source: idMap.get(e.source)!,
      target: idMap.get(e.target)!,
      data: { ...(e.data ?? {}) },
    }));

    // Connecting edge: target node → pasted root
    const connectingEdge = {
      id: `pasted_connect_${ts}`,
      source: nodeId,
      sourcePort: 'port-bottom',
      target: idMap.get(originalRoot.id)!,
      targetPort: 'port-top',
      routing: 'straight',
      data: { label: this._copiedBranch.rootLabel },
    };

    // If target was a leaf, promote it to branch (now it has a child)
    if ((targetNode.data as any)['isLeaf']) {
      this.modelService.updateNodeData(nodeId, { ...(targetNode.data as any), isLeaf: false });
    }

    this.modelService.addNodes(newNodes);
    this.modelService.addEdges([...newEdges, connectingEdge]);

    // Select the pasted root (mirrors WPF SelectPastedBranch)
    this.selectedNodeId = idMap.get(originalRoot.id)!;

    this.markDirty();
    this.closeContextMenu();
    this.toastService.show(`Branch "${this._copiedBranch.rootLabel}" pasted.`, { classname: 'bg-success text-light' });
  }

  private collapseNode(nodeId: string): void {
    const descendants = this.getAllDescendants(nodeId);
    if (descendants.length === 0) return;

    const descendantIds = descendants.map((n: any) => n.id);
    const edgesToHide: any[] = [];

    // Edges between the collapsed node and its children
    const directEdges = this.modelService
      .getConnectedEdges(nodeId)
      .filter((e: any) => e.source === nodeId);
    edgesToHide.push(...directEdges);

    // All edges within the subtree
    for (const id of descendantIds) {
      const edges = this.modelService.getConnectedEdges(id);
      edges.forEach((e: any) => edgesToHide.push(e));
    }

    const uniqueEdges = Array.from(new Map(edgesToHide.map((e: any) => [e.id, e])).values());

    this.hiddenDescendants.set(nodeId, {
      nodes: descendants.map((n: any) => ({ ...n })),
      edges: uniqueEdges.map((e: any) => ({ ...e })),
    });

    this.modelService.deleteEdges(uniqueEdges.map((e: any) => e.id));
    this.modelService.deleteNodes(descendantIds);
    this.collapsedNodeIds.add(nodeId);
    this.toastService.show('Node subtree collapsed.', { classname: 'bg-info text-dark' });
  }

  private expandNode(nodeId: string): void {
    const hidden = this.hiddenDescendants.get(nodeId);
    if (!hidden) return;

    this.modelService.addNodes(hidden.nodes);
    this.modelService.addEdges(hidden.edges);
    this.hiddenDescendants.delete(nodeId);
    this.collapsedNodeIds.delete(nodeId);
    this.toastService.show('Node subtree expanded.', { classname: 'bg-info text-dark' });
  }

  /** Export the current diagram model as a JSON file */
  saveTree(): void {
    try {
      const json = this.modelService.toJSON();
      this.downloadJson(json, 'decision-tree.json');
      this.markClean('decision-tree');
      this.toastService.show('Tree saved successfully.', { classname: 'bg-success text-light' });
    } catch {
      this.toastService.show('Save failed.', { classname: 'bg-danger text-light' });
    }
    this.closeContextMenu();
  }

  /** Save the current diagram tree to the database via POST /api/v1/c45/save */
  saveTreeToDb(): void {
    const tree = this.buildTreeFromDiagram();
    if (!tree) {
      this.toastService.show('Could not build tree — diagram is empty.', { classname: 'bg-warning text-dark' });
      return;
    }

    const payload: DecisionTreeData = {
      OutcomeColumn: this.originalModelData?.OutcomeColumn ?? '',
      FeatureOrder: this.originalModelData?.FeatureOrder ?? [],
      Schema: this.originalModelData?.Schema ?? [],
      Tree: tree,
    };

    this.isSaving = true;
    this.decisionTreeService.saveDecisionTree(payload, this.currentSavedModelId ?? undefined).subscribe({
      next: (res: C45SaveModelResponse) => {
        this.isSaving = false;
        this.currentSavedModelId = res.SavedModelId;
        this.markClean(this.currentTreeName);
        this.toastService.show(`Saved to database (ID: ${res.SavedModelId}).`, { classname: 'bg-success text-light' });
      },
      error: () => {
        this.isSaving = false;
        this.toastService.show('Save to database failed.', { classname: 'bg-danger text-light' });
      },
    });
  }

  /**
   * Walks the ng-diagram nodes/edges and reconstructs the C45 TreeNode tree.
   * Edge label conventions:
   *   "<= 0.5"  → numeric Left branch  (Threshold extracted from Left edge)
   *   "> 0.5"   → numeric Right branch
   *   "= value" → enum Children entry
   */
  private buildTreeFromDiagram(): TreeNode | null {
    const allNodes = this.modelService.nodes();
    const allEdges = this.modelService.edges();
    if (!allNodes.length) return null;

    const nodeIdsWithIncoming = new Set(allEdges.map((e: any) => e.target as string));
    const rootNode = allNodes.find((n: any) => !nodeIdsWithIncoming.has(n.id));
    if (!rootNode) return null;

    return this.buildTreeNode(rootNode.id, allNodes, allEdges);
  }

  private buildTreeNode(nodeId: string, allNodes: any[], allEdges: any[]): TreeNode {
    const node = allNodes.find((n: any) => n.id === nodeId);
    const data = (node?.data ?? {}) as any;

    if (data['isLeaf']) {
      return {
        IsLeaf: true,
        LeafLabel: data['label'] ?? null,
        LeafClassProbs: {},
        Feature: null,
        Threshold: null,
        Left: null,
        Right: null,
        Children: {},
      };
    }

    const outEdges = allEdges.filter((e: any) => e.source === nodeId);
    const children: Record<string, TreeNode> = {};
    let left: TreeNode | null = null;
    let right: TreeNode | null = null;
    let threshold: number | null = null;

    for (const edge of outEdges) {
      const label: string = (edge.data as any)?.['label'] ?? '';
      const childTree = this.buildTreeNode(edge.target, allNodes, allEdges);

      if (label.startsWith('<=')) {
        const val = parseFloat(label.replace('<=', '').trim());
        threshold = isNaN(val) ? null : val;
        left = childTree;
      } else if (label.startsWith('>')) {
        right = childTree;
      } else {
        // enum branch — strip leading "= " if present
        const key = label.startsWith('= ') ? label.slice(2) : label;
        children[key] = childTree;
      }
    }

    return {
      IsLeaf: false,
      LeafLabel: null,
      LeafClassProbs: {},
      Feature: data['label'] ?? null,
      Threshold: threshold,
      Left: left,
      Right: right,
      Children: children,
    };
  }

  /** Prompt to download the tree with a custom filename */
  saveAs(): void {
    try {
      const json = this.modelService.toJSON();
      const filename = prompt('Enter filename:', 'decision-tree.json') ?? 'decision-tree.json';
      const cleanName = filename.endsWith('.json') ? filename : `${filename}.json`;
      this.downloadJson(json, cleanName);
      this.markClean(cleanName.replace('.json', ''));
      this.toastService.show('Tree exported.', { classname: 'bg-success text-light' });
    } catch {
      this.toastService.show('Export failed.', { classname: 'bg-danger text-light' });
    }
    this.closeContextMenu();
  }

  /** Print the diagram by using the browser's print dialog */
  printSvg(): void {
    this.closeContextMenu();
    window.print();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private getAllDescendants(nodeId: string): any[] {
    const result: any[] = [];
    const queue: string[] = [nodeId];
    const visited = new Set<string>();

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      visited.add(current);

      const edges = this.modelService.getConnectedEdges(current);
      for (const edge of edges) {
        if (edge.source === current) {
          const child = this.modelService.getNodeById(edge.target);
          if (child) {
            result.push(child);
            queue.push(edge.target);
          }
        }
      }
    }

    return result;
  }

  /**
   * Exports the current diagram as a canonical .sfe JSON file,
   * matching the SLS.TreeEditor SfeFileModel format.
   */
  exportAsSfe(): void {
    const allNodes = this.modelService.nodes();
    const allEdges = this.modelService.edges();

    if (!allNodes.length) {
      this.toastService.show('Nothing to export.', { classname: 'bg-warning text-dark' });
      return;
    }

    // Determine root node (no incoming edges)
    const nodeIdsWithIncoming = new Set(allEdges.map((e: any) => e.target as string));
    const rootNode = allNodes.find((n: any) => !nodeIdsWithIncoming.has(n.id));
    if (!rootNode) {
      this.toastService.show('Could not identify root node.', { classname: 'bg-warning text-dark' });
      return;
    }

    const sfeNodes: any[] = [];
    const sfeLinks: any[] = [];

    for (const node of allNodes) {
      const data = (node.data as any) ?? {};
      const isLeaf: boolean = !!data['isLeaf'];
      const isRoot: boolean = node.id === rootNode.id;

      // Default fill colours matching WPF ApplyDefaultStyles
      let fillColor = '#D9D9D9';
      if (isRoot) fillColor = '#F08700';
      else if (isLeaf) fillColor = '#78B928';

      sfeNodes.push({
        Id: node.id,
        NodeType: isLeaf ? 'leaf' : 'decision',
        Text: data['label'] ?? '',
        FieldName: isLeaf ? null : (data['label'] ?? null),
        Value: isLeaf ? (data['label'] ?? null) : null,
        X: node.position?.x ?? 0,
        Y: node.position?.y ?? 0,
        Url: null,
        Description: null,
        CatalogPages: null,
        FillColor: fillColor,
      });
    }

    for (const edge of allEdges) {
      const edgeData = (edge.data as any) ?? {};
      const label: string = edgeData['label'] ?? '';
      sfeLinks.push({
        Id: edge.id,
        FromNodeId: edge.source,
        ToNodeId: edge.target,
        Text: label,
        Value: label,
        Color: '#D9D9D9',
        LinkType: 'Straight',
        TextPosition: 'Center',
      });
    }

    const treeName = this.currentTreeName === 'Unsaved' ? 'decision-tree' : this.currentTreeName;

    const sfeFile = {
      Format: 'schuco.sfe.tree',
      Version: '1.0',
      TreeId: rootNode.id,
      Name: treeName,
      RootNodeId: rootNode.id,
      OutputField: 'Output',
      OutputType: 'string',
      Nodes: sfeNodes,
      Links: sfeLinks,
    };

    const json = JSON.stringify(sfeFile, null, 2);
    const blob = new Blob([json], { type: 'application/json' });

    if (typeof (window as any).showSaveFilePicker === 'function') {
      // Modern browsers: open native OS Save As dialog
      (window as any).showSaveFilePicker({
        suggestedName: `${treeName}.sfe`,
        types: [{ description: 'SFE Tree File', accept: { 'application/json': ['.sfe'] } }],
      }).then((fileHandle: any) => fileHandle.createWritable()).then((writable: any) => {
        return writable.write(blob).then(() => writable.close());
      }).then(() => {
        this.toastService.show(`Exported as ${treeName}.sfe.`, { classname: 'bg-success text-light' });
      }).catch((err: any) => {
        if (err?.name !== 'AbortError') {
          this.toastService.show('Export failed.', { classname: 'bg-danger text-light' });
        }
      });
    } else {
      // Fallback for browsers without File System Access API
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${treeName}.sfe`;
      a.click();
      URL.revokeObjectURL(url);
      this.toastService.show(`Exported as ${treeName}.sfe.`, { classname: 'bg-success text-light' });
    }
  }

  /**
   * Re-arranges all visible nodes using the same top-down, leaf-width-proportional
   * layout as the initial load, then fits the viewport to show the whole tree.
   */
  autoArrange(): void {
    const allNodes = this.modelService.nodes();
    const allEdges = this.modelService.edges();
    if (!allNodes.length) {
      this.toastService.show('Nothing to arrange.', { classname: 'bg-warning text-dark' });
      return;
    }

    // Find root: node with no incoming edge
    const nodeIdsWithIncoming = new Set(allEdges.map((e: any) => e.target as string));
    const rootNode = allNodes.find((n: any) => !nodeIdsWithIncoming.has(n.id));
    if (!rootNode) {
      this.toastService.show('Could not find root node.', { classname: 'bg-warning text-dark' });
      return;
    }

    // Count leaf nodes in a subtree (leaf = no outgoing edges)
    const countLeaves = (nodeId: string): number => {
      const childEdges = allEdges.filter((e: any) => e.source === nodeId);
      if (childEdges.length === 0) return 1;
      return childEdges.reduce((sum: number, e: any) => sum + countLeaves(e.target), 0);
    };

    // Recursively assign x/y positions
    const positionMap = new Map<string, { x: number; y: number }>();
    const assignPositions = (nodeId: string, depth: number, xStart: number, subtreeWidth: number): void => {
      const x = xStart + subtreeWidth / 2 - NODE_WIDTH / 2;
      const y = depth * (NODE_HEIGHT + Y_GAP);
      positionMap.set(nodeId, { x, y });

      const childEdges = allEdges.filter((e: any) => e.source === nodeId);
      let childX = xStart;
      for (const edge of childEdges) {
        const childLeaves = countLeaves(edge.target);
        const childWidth = childLeaves * (NODE_WIDTH + X_GAP);
        assignPositions(edge.target, depth + 1, childX, childWidth);
        childX += childWidth;
      }
    };

    const totalLeaves = countLeaves(rootNode.id);
    const totalWidth = totalLeaves * (NODE_WIDTH + X_GAP);
    assignPositions(rootNode.id, 0, 0, totalWidth);

    // Apply computed positions to all nodes
    for (const [id, position] of positionMap) {
      this.modelService.updateNode(id, { position });
    }

    // Fit viewport to show the whole arranged tree
    this.viewportService.zoomToFit({ padding: 80 });
    this.toastService.show('Tree auto-arranged.', { classname: 'bg-info text-dark' });
  }

  /** Marks the tree as having unsaved changes (mirrors WPF MarkTreeAsDirty) */
  private markDirty(): void {
    this.hasUnsavedChanges = true;
  }

  /** Marks the tree as saved and updates the display name (mirrors WPF MarkTreeAsClean + UpdateCurrentTreeDisplay) */
  private markClean(name: string): void {
    this.hasUnsavedChanges = false;
    this.currentTreeName = name;
  }

  private downloadJson(json: string, filename: string): void {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ---------------------------------------------------------------------------
  // Tree building (unchanged from original)
  // ---------------------------------------------------------------------------

  private countLeaves(node: TreeNode): number {
    if (node.IsLeaf) return 1;
    const children = Object.values(node.Children);
    if (children.length > 0) {
      return children.reduce((sum, child) => sum + this.countLeaves(child), 0);
    }
    const leftLeaves = node.Left ? this.countLeaves(node.Left) : 0;
    const rightLeaves = node.Right ? this.countLeaves(node.Right) : 0;
    return (leftLeaves + rightLeaves) || 1;
  }

  private buildGraph(
    node: TreeNode,
    nodeId: string,
    condition: string,
    depth: number,
    xStart: number,
    subtreeWidth: number,
    nodes: any[],
    edges: any[]
  ): void {
    const x = xStart + subtreeWidth / 2 - NODE_WIDTH / 2;
    const y = depth * (NODE_HEIGHT + Y_GAP);

    nodes.push({
      id: nodeId,
      type: 'tree-node',
      position: { x, y },
      autoSize: true,
      data: { label: node.IsLeaf ? node.LeafLabel : node.Feature, isLeaf: node.IsLeaf, isRoot: depth === 0 },
    });

    if (!node.IsLeaf) {
      const hasChildren = Object.keys(node.Children).length > 0;

      if (hasChildren) {
        let childX = xStart;
        Object.entries(node.Children).forEach(([key, child], i) => {
          const childId = `${nodeId}_c${i}`;
          const childLeaves = this.countLeaves(child);
          const childWidth = childLeaves * (NODE_WIDTH + X_GAP);
          this.buildGraph(child, childId, key, depth + 1, childX, childWidth, nodes, edges);
          edges.push({
            id: `edge_${nodeId}_${childId}`,
            source: nodeId,
            sourcePort: 'port-bottom',
            target: childId,
            targetPort: 'port-top',
            routing: 'straight',
            data: { label: `= ${key}` },
          });
          childX += childWidth;
        });
      } else {
        const binaryChildren: [string, TreeNode, string][] = [];
        if (node.Left) binaryChildren.push([`${nodeId}_L`, node.Left, `<= ${node.Threshold ?? ''}`]);
        if (node.Right) binaryChildren.push([`${nodeId}_R`, node.Right, `> ${node.Threshold ?? ''}`]);

        let childX = xStart;
        binaryChildren.forEach(([childId, child, label]) => {
          const childLeaves = this.countLeaves(child);
          const childWidth = childLeaves * (NODE_WIDTH + X_GAP);
          this.buildGraph(child, childId, label, depth + 1, childX, childWidth, nodes, edges);
          edges.push({
            id: `edge_${nodeId}_${childId}`,
            source: nodeId,
            sourcePort: 'port-bottom',
            target: childId,
            targetPort: 'port-top',
            routing: 'straight',
            data: { label },
          });
          childX += childWidth;
        });
      }
    }
  }
}

